import { formatMoney } from '@/utils/format';
import type { ChannelPosition } from '@/utils/ledger';

interface ChannelPositionCardProps {
  positions: ChannelPosition[];
  currencies: string[];
}

const toneClasses: Record<string, string> = {
  wallet: 'bg-primary-100 text-primary-700',
  mobile: 'bg-secondary-100 text-secondary-800',
  bank: 'bg-accent-100 text-accent-700',
};

/** Each channel's colour in the share bar. */
const barClasses: Record<string, string> = {
  wallet: 'bg-primary-500',
  mobile: 'bg-secondary-500',
  bank: 'bg-accent-500',
};

export default function ChannelPositionCard({ positions, currencies }: ChannelPositionCardProps) {
  const total = positions.reduce((sum, item) => sum + item.amountKes, 0);
  const shareOf = (amount: number) => (total > 0 ? Math.round((amount / total) * 100) : 0);
  return (
    <section className="rounded-lg border border-background-200 bg-background-50">
      <header className="flex flex-wrap items-center justify-between gap-2 border-b border-background-100 px-5 py-4">
        <div>
          <h2 className="font-heading text-sm font-semibold text-foreground-950">
            Payout position by channel
          </h2>
          <p className="mt-0.5 text-xs text-foreground-500">
            Where payouts left the funding wallets — KES equivalent
          </p>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {currencies.map((currency) => (
            <span
              key={currency}
              className="rounded-full border border-background-200 bg-background-100 px-2 py-0.5 text-[11px] font-label font-medium text-foreground-600"
            >
              {currency}
            </span>
          ))}
        </div>
      </header>
      <div className="grid grid-cols-1 divide-y divide-background-100 sm:grid-cols-3 sm:divide-x sm:divide-y-0">
        {positions.map((item) => (
          <div key={item.channel} className="flex items-center gap-3 px-5 py-4">
            <span
              className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-md text-lg ${toneClasses[item.channel]}`}
            >
              <i className={item.icon} />
            </span>
            <div className="min-w-0">
              <p className="text-xs font-label font-medium text-foreground-500">{item.label}</p>
              <p className="mt-0.5 font-heading text-lg font-semibold text-foreground-950">
                {formatMoney(item.amountKes, 'KES')}
              </p>
              <p className="text-[11px] text-foreground-400">
                {item.count} line{item.count === 1 ? '' : 's'}
                {total > 0 ? <span className="font-medium text-foreground-600"> · {shareOf(item.amountKes)}% of payouts</span> : null}
              </p>
            </div>
          </div>
        ))}
      </div>
      {total > 0 ? (
        // How the payouts split across channels, growing in from the left.
        <div className="px-5 pb-4">
          <div className="flex h-2 overflow-hidden rounded-full bg-background-200" aria-hidden="true">
            {positions.map((item, index) =>
              item.amountKes > 0 ? (
                <span
                  key={item.channel}
                  className={`share-grow h-full ${barClasses[item.channel] ?? 'bg-foreground-400'} ${index > 0 ? 'border-l-2 border-background-50' : ''}`}
                  style={{ width: `${(item.amountKes / total) * 100}%`, animationDelay: `${index * 120}ms` }}
                />
              ) : null,
            )}
          </div>
        </div>
      ) : null}
    </section>
  );
}